import React from "react";
import clsx from "clsx";

interface SpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  /** Custom color class (defaults to primary) */
  color?: string;
  /** Accessible label for the spinner */
  label?: string;
}

/**
 * Spinner component for loading states
 *
 * Best Practice: Always provide context for why something is loading.
 * Use the label prop or wrap with descriptive text.
 */
const Spinner: React.FC<SpinnerProps> = ({
  size = "md",
  className,
  color = "text-primary-600",
  label = "Loading",
}) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  return (
    <div
      className={clsx("inline-flex items-center justify-center", className)}
      role="status"
      aria-label={label}
    >
      <svg
        className={clsx("animate-spin motion-reduce:animate-none", color, sizeClasses[size])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  );
};

interface LoadingProps {
  fullScreen?: boolean;
  text?: string;
  /** Size of the spinner */
  size?: SpinnerProps["size"];
  /** Shows a backdrop overlay */
  overlay?: boolean;
}

/**
 * Loading component with optional text
 *
 * Best Practice: Provide loading text when the wait time may be noticeable.
 * This gives users context about what's happening.
 */
export const Loading: React.FC<LoadingProps> = ({
  fullScreen = false,
  text,
  size = "lg",
  overlay = false,
}) => {
  const content = (
    <div
      className="flex flex-col items-center justify-center space-y-4"
      role="status"
      aria-live="polite"
    >
      <Spinner size={size} label={text || "Loading"} />
      {text && <p className="text-gray-600 dark:text-gray-300 font-medium">{text}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className={clsx(
          "fixed inset-0 flex items-center justify-center z-50",
          overlay
            ? "bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm"
            : "bg-white dark:bg-neutral-900"
        )}
        aria-busy="true"
      >
        {content}
      </div>
    );
  }

  return content;
};

/**
 * Inline loading indicator for buttons or small areas
 */
export const InlineLoading: React.FC<{
  text?: string;
  className?: string;
}> = ({ text = "Loading...", className }) => (
  <span className={clsx("inline-flex items-center gap-2", className)} role="status">
    <Spinner size="sm" label={text} />
    <span className="text-sm text-neutral-600 dark:text-neutral-400">{text}</span>
  </span>
);

/**
 * Page loading placeholder for lazy-loaded routes
 */
export const PageLoading: React.FC<{ text?: string }> = ({ text = "Loading page..." }) => (
  <div className="flex items-center justify-center min-h-[50vh]" role="status" aria-label={text}>
    <div className="flex flex-col items-center space-y-4">
      <Spinner size="xl" label={text} />
      <p className="text-neutral-500 dark:text-neutral-400 animate-pulse motion-reduce:animate-none">
        {text}
      </p>
    </div>
  </div>
);

export default Spinner;
